import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Linkedin, Instagram, Facebook, Mail, Phone, MapPin } from 'lucide-react';
import { footerData } from '../mockData';

const Footer = () => {
  const socialIcons = {
    Linkedin: Linkedin,
    Instagram: Instagram,
    Facebook: Facebook,
  };

  return (
    <footer className="bg-[#FAFAFA] border-t border-gray-100" data-testid="main-footer">
      <div className="container mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <img
              src="/logo.png"
              alt="Hei Web Creations logo"
              className="h-12 w-auto"
            />
            <p className="text-gray-500 text-sm leading-relaxed">
              {footerData.tagline}
            </p>
            <div className="flex space-x-3">
              {footerData.social.map((social) => {
                const Icon = socialIcons[social.icon];
                return (
                  <motion.a
                    key={social.platform}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.1, y: -2 }}
                    className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-[#C8102E] hover:text-white hover:border-[#C8102E] transition-all duration-300"
                    data-testid={`social-link-${social.platform.toLowerCase()}`}
                  >
                    <Icon size={18} />
                  </motion.a>
                );
              })}
            </div>
          </motion.div>

          {/* Services */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <h3 className="text-sm font-semibold text-[#1A1A1A] mb-6 uppercase tracking-wider">Services</h3>
            <ul className="space-y-3">
              {footerData.links.services.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.url}
                    className="text-gray-500 hover:text-[#C8102E] transition-colors duration-300 text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Company */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h3 className="text-sm font-semibold text-[#1A1A1A] mb-6 uppercase tracking-wider">Company</h3>
            <ul className="space-y-3">
              {footerData.links.company.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.url}
                    className="text-gray-500 hover:text-[#C8102E] transition-colors duration-300 text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <h3 className="text-sm font-semibold text-[#1A1A1A] mb-6 uppercase tracking-wider">Contact</h3>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3 text-gray-500 text-sm">
                <MapPin size={18} className="mt-0.5 flex-shrink-0 text-[#C8102E]" />
                <span>{footerData.address}</span>
              </li>
              <li className="flex items-center space-x-3 text-gray-500 text-sm">
                <Mail size={18} className="flex-shrink-0 text-[#C8102E]" />
                <a
                  href={`mailto:${footerData.email}`}
                  className="hover:text-[#C8102E] transition-colors duration-300"
                >
                  {footerData.email}
                </a>
              </li>
              <li className="flex items-center space-x-3 text-gray-500 text-sm">
                <Phone size={18} className="flex-shrink-0 text-[#C8102E]" />
                <a
                  href={`tel:${footerData.phone}`}
                  className="hover:text-[#C8102E] transition-colors duration-300"
                >
                  {footerData.phone}
                </a>
              </li>
            </ul>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-12 pt-8 border-t border-gray-200 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0"
        >
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} {footerData.company}. All rights reserved.
          </p>
          <div className="flex space-x-6 text-sm text-gray-500">
            <Link to="/privacy-policy" className="hover:text-[#C8102E] transition-colors duration-300" data-testid="footer-privacy-link">
              Privacy Policy
            </Link>
            <Link to="/terms-of-service" className="hover:text-[#C8102E] transition-colors duration-300" data-testid="footer-terms-link">
              Terms of Service
            </Link>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
